import { UserMenu, useMe } from "@nexxt/account-ui";
import { AppFrame, Bell, ViewerProvider } from "@nexxt/app-kit";
import { LogoMark, Sidebar, SidebarItem, SidebarPlaces, useMediaQuery, wideLayout } from "@nexxt/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Building2,
  CalendarRange,
  FileMinus,
  House,
  type LucideIcon,
  PackageOpen,
  Rows3,
  ShieldCheck,
  ShoppingCart,
  Tags,
  Undo2,
  UserRound,
} from "lucide-react";
import type { MouseEvent } from "react";
import { m } from "../../paraglide/messages.js";
import { bellClient } from "../bell-client";
import { followClient } from "../follow-client";

// The frame of Atelier (ADR-154): Nexxt on the left and the organization of the user next to it, so that the user
// recognizes its own space first (brand/README.md §8); the places, few and always the same (principi-design.md), and
// those of a domain only with the permission to read it. No search and no shortcuts until there is more to look for.

interface Place {
  to:
    | "/"
    | "/season-calendar"
    | "/linesheets"
    | "/attributes"
    | "/purchase-orders"
    | "/inbounds"
    | "/credit-notes"
    | "/vendor-returns"
    | "/organization"
    | "/account"
    | "/security";
  label: () => string;
  icon: LucideIcon;
  permission?: string;
}

const PLACES: Place[] = [
  { to: "/", label: m.nav_home, icon: House },
  // ADR-325: the deadlines of the lists below, of the brand and of the supplier
  { to: "/season-calendar", label: m.nav_season_calendar, icon: CalendarRange },
  { to: "/linesheets", label: m.nav_linesheets, icon: Rows3, permission: "linesheet.read" },
  { to: "/attributes", label: m.nav_attributes, icon: Tags, permission: "attributes.read" },
  { to: "/purchase-orders", label: m.nav_purchase_orders, icon: ShoppingCart, permission: "purchase_order.read" },
  { to: "/inbounds", label: m.nav_inbounds, icon: PackageOpen, permission: "inbound.read" },
  { to: "/credit-notes", label: m.nav_credit_notes, icon: FileMinus, permission: "inbound.read" },
  { to: "/vendor-returns", label: m.nav_vendor_returns, icon: Undo2, permission: "vendor_return.read" },
  { to: "/organization", label: m.nav_organization, icon: Building2, permission: "organization.read" },
  { to: "/account", label: m.nav_account, icon: UserRound },
  { to: "/security", label: m.nav_security, icon: ShieldCheck },
];

export function AtelierShell() {
  const { permissions, mfaEnrollmentRequired } = useMe();
  const wide = useMediaQuery(wideLayout);
  return (
    // the screens of the domains (ADR-166) for an external user: never one of Tomorrow
    <ViewerProvider value={{ isInternal: false, permissions }}>
      <AppFrame
        product="atelier"
        sidebar={({ variant, closeMenu }) => (
          <AtelierSidebar variant={variant} {...(variant === "sheet" && { onFollowLink: closeMenu })} />
        )}
        topBar={() => (
          <>
            <OrganizationName className="min-w-0 flex-1 truncate border-l border-border pl-2 text-sm" />
            {/* a session that owes a second factor reaches only the page that adds one (ADR-151) */}
            {!wide && !mfaEnrollmentRequired && <Bell client={bellClient} follows={followClient} variant="icon" />}
            <UserMenu compact />
          </>
        )}
      />
    </ViewerProvider>
  );
}

function OrganizationName({ className }: { className: string }) {
  return <p className={className}>{useMe().organization.name}</p>;
}

function AtelierSidebar({ variant, onFollowLink }: { variant: "column" | "sheet"; onFollowLink?: () => void }) {
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // a session that owes a second factor reaches only the page that adds one (ADR-151)
  const places = me.mfaEnrollmentRequired
    ? PLACES.filter((place) => place.to === "/security")
    : PLACES.filter((place) => !place.permission || me.permissions.includes(place.permission));
  return (
    <Sidebar
      label={m.nav_label()}
      className={variant === "column" ? "w-60 shrink-0 border-r border-border bg-muted px-3 py-3" : "flex-1 p-3"}
      {...(onFollowLink && {
        onClick: (e: MouseEvent) => {
          if (e.target instanceof Element && e.target.closest("a")) onFollowLink();
        },
      })}
    >
      <div className="grid gap-0.5 px-2 py-2">
        <LogoMark product="atelier" />
        <OrganizationName className="truncate text-sm font-medium" />
      </div>
      <SidebarPlaces className="mt-2 pb-2">
        {places.map((place) => (
          <SidebarItem
            key={place.to}
            render={<Link to={place.to} />}
            icon={<place.icon aria-hidden />}
            label={place.label()}
            active={pathname === place.to || (place.to !== "/" && pathname.startsWith(`${place.to}/`))}
          />
        ))}
      </SidebarPlaces>
      <div className="flex flex-col gap-1 border-t border-border pt-2">
        {/* below `lg` the bell is in the top bar: a sheet opened from the menu would close with it */}
        {variant === "column" && !me.mfaEnrollmentRequired && (
          <Bell client={bellClient} follows={followClient} variant="item" />
        )}
        <UserMenu />
      </div>
    </Sidebar>
  );
}
